import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, MapIcon, SparklesIcon, SwordIcon, ShieldIcon, HeartIcon, CrownIcon, UsersIcon, ChevronDownIcon, ChevronUpIcon, CircleDotIcon, ArrowRightIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-113">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-114">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-#[f5f0e8] mb-6 tracking-tight" data-id="element-115">
      {title} <span className="text-[#d4a017]" data-id="element-116">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-117"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-118">
        {description}
      </p>}
  </motion.div>;
/* ─── OVERVIEW ─── */
export function OverviewSection() {
  return <section id="overview" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-119">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/10 to-transparent" data-id="element-120"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-121">
        {sectionHeader('Section 01', 'Project', 'Overview', 'Understanding the immersive cultural experience that is redefining how we engage with heritage.')}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-122">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-123">
            <div className="aspect-[4/3] relative overflow-hidden border border-[#d4a017]/10" data-id="element-124">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms immersive experience" className="w-full h-full object-cover" data-id="element-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-transparent" data-id="element-126"></div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="space-y-6" data-id="element-127">
            <p className="text-[#f5f0e8]/70 text-lg leading-relaxed font-light" data-id="element-128">
              <strong className="text-[#d4a017] font-medium" data-id="element-129">
                Life in the Sikh Kingdoms
              </strong>{' '}
              is a 90-minute immersive dining and cultural experience at
              Toronto's Illuminarium. Guests step into the court of Maharaja
              Ranjit Singh through 360° projections, royal Punjabi cuisine,
              interactive technology, and multi-sensory storytelling.
            </p>
            <p className="text-[#f5f0e8]/50 leading-relaxed font-light" data-id="element-130">
              This is not a museum exhibit. It is a living, breathing portal to
              1825 Lahore — where history unfolds not behind glass, but all
              around you. Through walls that transform, floors that transport,
              and flavors that resurrect a golden age.
            </p>
            <p className="text-[#f5f0e8]/50 leading-relaxed font-light" data-id="element-131">
              Honoring the groundbreaking "Art of the Sikh Kingdoms" exhibition
              at the Royal Ontario Museum — the second most popular exhibition
              in ROM history — this experience brings that legacy into the
              immersive age.
            </p>
          </motion.div>
        </div>

        {/* Infographic strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-132">
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-133">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-134">5</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-135">
              Portals
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-136">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-137">360°</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-138">
              Projections
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-139">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-140">90</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-141">
              Minutes
            </p>
          </div>
          <div className="border border-[#d4a017]/15 bg-[#0f0f23]/60 p-6 text-center" data-id="element-142">
            <p className="font-serif text-3xl text-[#d4a017] mb-1" data-id="element-143">120</p>
            <p className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase font-light" data-id="element-144">
              Guests
            </p>
          </div>
        </motion.div>

        {/* Architecture Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-16" data-id="element-145">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-146">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-147">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/Kezdo9VonAo" title="Architecture of the Experience" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-148"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-149">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-150">
                Architecture of the Experience
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-151">
                How the immersive space is designed — portals, projections,
                floor map, and spatial storytelling.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── GUEST JOURNEY ─── */
const journeyPhases = [{
  phase: 'Arrival',
  time: '0 min',
  description: 'Guests arrive at the Illuminarium Toronto. Ambient soundscape begins. Subtle saffron lighting guides them toward the entrance corridor.',
  detail: 'Check-in, welcome drink, initial sensory priming with ambient Sikh court music.'
}, {
  phase: 'Holding Room',
  time: '5 min',
  description: 'A transitional chamber where the story begins. Narration introduces Maharaja Ranjit Singh and the Sikh Empire. The portrait is revealed — fragmented, incomplete.',
  detail: 'Guests learn that five spirits must be awakened to restore the portrait. The quest begins.'
}, {
  phase: 'Main Room Entry',
  time: '10 min',
  description: 'The doors open to the full 360° immersive space. The living map of Punjab illuminates the floor. Five rivers glow. City nodes pulse.',
  detail: 'Guests orient themselves on the map. The first portal activation circle begins to glow.'
}, {
  phase: 'Portal Activations',
  time: '15–75 min',
  description: 'Five portals activate sequentially. Each portal is a complete cinematic chapter — visual, culinary, aromatic, and interactive.',
  detail: 'Akali Phula Singh → Lehna Singh Majithia → Hari Singh Nalwa → Fakir Azizuddin → Maharani Jind Kaur. Each restores a fragment of the portrait.'
}, {
  phase: 'Portrait Awakening',
  time: '75 min',
  description: 'All five fragments unite. The Lahore Darbar painting comes alive across the entire room. The court assembles. Maharaja Ranjit Singh appears.',
  detail: 'A unity gesture from all guests triggers the final reveal. The room transforms into the full court of Lahore.'
}, {
  phase: 'Exit & Legacy',
  time: '85 min',
  description: 'Guests exit through a reflection corridor. QR codes for digital souvenirs, merchandise, and sharing. The experience lives on.',
  detail: 'Photo moments, digital collectibles, merchandise area, and community sharing wall.'
}];
export function GuestJourneySection() {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  return <section id="guest-journey" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-152">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1a1a2e]/5 to-transparent" data-id="element-153"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-4xl" data-id="element-154">
        {sectionHeader('Section 02', 'Guest', 'Journey', 'The complete arc from arrival to legacy — every phase designed for maximum immersion.')}

        <div className="relative" data-id="element-155">
          {/* Timeline line */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#d4a017]/30 via-[#d4a017]/10 to-transparent" data-id="element-156"></div>

          <div className="space-y-4" data-id="element-157">
            {journeyPhases.map((phase, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="relative pl-16 md:pl-20" data-id="element-158">
                {/* Timeline dot */}
                <div className="absolute left-4 md:left-6 top-6 w-4 h-4 rounded-full border-2 border-[#d4a017]/40 bg-[#0a0a0f] flex items-center justify-center" data-id="element-159">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#d4a017]" data-id="element-160"></div>
                </div>

                <button onClick={() => setExpandedPhase(expandedPhase === i ? null : i)} className="w-full text-left border border-#[d4a017]/10 hover:border-#[d4a017]/25 bg-#[0f0f23]/40 hover:bg-#[0f0f23]/60 p-6 transition-all duration-300" data-id="element-161">
                  <div className="flex items-start justify-between gap-4" data-id="element-162">
                    <div className="flex-1" data-id="element-163">
                      <div className="flex items-center gap-3 mb-2" data-id="element-164">
                        <span className="text-[#d4a017]/40 text-[10px] tracking-wider uppercase font-mono" data-id="element-165">
                          {phase.time}
                        </span>
                      </div>
                      <h4 className="font-serif text-xl text-[#f5f0e8] mb-2" data-id="element-166">
                        {phase.phase}
                      </h4>
                      <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-167">
                        {phase.description}
                      </p>
                    </div>
                    {expandedPhase === i ? <ChevronUpIcon className="w-4 h-4 text-[#d4a017] flex-shrink-0 mt-1" data-id="element-168" /> : <ChevronDownIcon className="w-4 h-4 text-[#d4a017]/30 flex-shrink-0 mt-1" data-id="element-169" />}
                  </div>
                </button>

                <AnimatePresence data-id="element-170">
                  {expandedPhase === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-171">
                      <div className="border border-t-0 border-[#d4a017]/10 bg-[#d4a017]/5 px-6 py-4" data-id="element-172">
                        <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-173">
                          {phase.detail}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
/* ─── FIVE PORTALS ─── */
const portals = [{
  name: 'Akali Phula Singh',
  represents: 'Spirit & Fearlessness',
  tagline: 'The warrior-saint who embodied the Khalsa spirit',
  why: 'He represents the unbreakable spirit of the Sikh faith — the courage to stand for truth regardless of consequence. His portal awakens the fire within.',
  feel: 'Adrenaline, reverence, and the thundering energy of the Khalsa cavalry charging across the plains.',
  look: 'Crimson and gold projections. Dust storms. Cavalry formations. The Nishan Sahib flag rippling across walls.',
  sensory: 'Sound of war drums and Jaikara. Scent of gunpowder and earth. A bold, spiced bite — warrior’s fuel!',
  restoration: 'Restores the spirit fragment — the fire in the portrait’s eyes!'
}, {
  name: 'Lehna Singh Majithia',
  represents: 'Structure & Innovation',
  tagline: 'The master builder who modernized the empire',
  why: 'He represents the engineering genius of the Sikh Empire — the cannons, the forts, the infrastructure that made Lahore a wonder of the world.',
  feel: 'Precision, wonder, and the satisfaction of watching something magnificent being built before your eyes.',
  look: 'Blueprint projections. Architectural schematics. Cannon foundries. The walls of Lahore Fort rising from the floor.',
  sensory: 'Sound of hammers and forges. Scent of heated metal and sandalwood. A structured, layered bite — precision on a plate.',
  restoration: 'Restores the structure fragment — the architecture of the portrait’s background.'
}, {
  name: 'Hari Singh Nalwa',
  represents: 'Protection & Expansion',
  tagline: 'The legendary general who guarded the frontier',
  why: 'He represents the protective strength of the empire — the general so feared that his name alone held the Khyber Pass.',
  feel: 'Awe, safety, and the vastness of an empire that stretched from Lahore to Kabul.',
  look: 'Mountain panoramas. The Khyber Pass. Eagle-eye perspectives soaring over the frontier. Fortress walls.',
  sensory: 'Sound of mountain winds and eagle cries. Scent of pine and cold stone. A bold, protective bite — strength in every layer.',
  restoration: 'Restores the protection fragment — the borders and boundaries of the portrait.'
}, {
  name: 'Fakir Azizuddin',
  represents: 'Pluralism & Diplomacy',
  tagline: 'The Muslim diplomat who served the Sikh court',
  why: "He represents the pluralism of the Sikh Empire — a Muslim serving as chief diplomat in a Sikh court, embodying the empire's radical inclusivity.",
  feel: 'Warmth, connection, and the beauty of a court where faith was respected but never a barrier.',
  look: 'Diplomatic chambers. Treaty scrolls. The Koh-i-Noor diamond. Soft candlelight and rich fabrics.',
  sensory: 'Sound of courtly music and gentle conversation. Scent of rose and amber. A refined, diplomatic bite — elegance and balance.',
  restoration: "Restores the pluralism fragment — the diverse faces in the portrait's court."
}, {
  name: 'Maharani Jind Kaur',
  represents: 'Continuity & Resilience',
  tagline: 'The queen who fought to preserve the kingdom',
  why: 'She represents the continuity of the Sikh spirit — the mother, the queen, the political force who refused to let the empire die quietly.',
  feel: "Emotion, resilience, and the fierce love of a mother fighting for her son’s throne and her people's future.",
  look: 'Palace interiors. Candlelit chambers. Letters being written. The young Duleep Singh. Twilight over Lahore.',
  sensory: "Sound of a mother's lullaby and political whispers. Scent of jasmine and ink. A tender, complex bite — sweetness with depth.",
  restoration: 'Restores the continuity fragment — the heart of the portrait, completing the Lahore Darbar.'
}];
export function FivePortalsSection() {
  const [activePortal, setActivePortal] = useState(0);
  return <section id="five-portals" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-174">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-175"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-6xl" data-id="element-176">
        {sectionHeader('Section 03', 'The Five', 'Portals', 'Five historical figures. Five cinematic chapters. Five fragments of a shattered portrait.')}

        {/* Portal tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-177">
          {portals.map((portal, i) => <button key={i} onClick={() => setActivePortal(i)} className={`px-4 py-2.5 text-xs tracking-[0.1em] uppercase transition-all duration-300 border ${activePortal === i ? 'bg-[#d4a017] text-[#0a0a0f] border-[#d4a017] font-medium' : 'border-#[d4a017]/20 text-#[f5f0e8]/40 hover:text-#[f5f0e8]/70 hover:border-#[d4a017]/40'}`} data-id="element-178">
              {portal.name.split(' ').pop()}
            </button>)}
        </div>

        {/* Active portal detail */}
        <AnimatePresence mode="wait" data-id="element-179">
          <motion.div key={activePortal} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.4
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/50" data-id="element-180">
            <div className="p-8 md:p-12" data-id="element-181">
              <div className="flex flex-col md:flex-row md:items-start gap-8 mb-10" data-id="element-182">
                <div className="flex-1" data-id="element-183">
                  <p className="text-[#d4a017]/50 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-184">
                    Portal {activePortal + 1} of 5
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-2" data-id="element-185">
                    {portals[activePortal].name}
                  </h3>
                  <p className="text-[#d4a017] text-sm tracking-wider uppercase font-light mb-4" data-id="element-186">
                    {portals[activePortal].represents}
                  </p>
                  <p className="text-[#f5f0e8]/50 font-light italic" data-id="element-187">
                    {portals[activePortal].tagline}
                  </p>
                </div>
                <div className="w-24 h-24 md:w-32 md:h-32 border border-#[d4a017]/20 bg-#[d4a017]/5 flex items-center justify-center flex-shrink-0" data-id="element-188">
                  <span className="font-serif text-5xl md:text-6xl text-[#d4a017]/20" data-id="element-189">
                    {activePortal + 1}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-190">
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-191">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-192">
                    Why They Matter
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-193">
                    {portals[activePortal].why}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-194">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-195">
                    What Guests Feel
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-196">
                    {portals[activePortal].feel}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-197">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-198">
                    Visual World
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-199">
                    {portals[activePortal].look}
                  </p>
                </div>
                <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-200">
                  <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-201">
                    Sound / Scent / Bite
                  </p>
                  <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-202">
                    {portals[activePortal].sensory}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#d4a017]/10" data-id="element-203">
                <p className="text-[#d4a017]/60 text-[10px] tracking-[0.2em] uppercase mb-2" data-id="element-204">
                  Portrait Restoration
                </p>
                <p className="text-[#d4a017]/80 font-light italic" data-id="element-205">
                  {portals[activePortal].restoration}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Portal & AI Design Shots Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-206">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-207">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-208">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/MvFUiMZz9fc" title="Portal & AI Design Shots" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-209"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-210">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-211">
                Portal &amp; AI Design Shots
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-212">
                Visual explorations of the five portals — AI-generated concept
                art and design direction for each character world.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── STORY ARC ─── */
const arcPhases = [{
  label: 'Spirit',
  portal: 'Akali Phula Singh',
  emotion: 'Awakening'
}, {
  label: 'Structure',
  portal: 'Lehna Singh Majithia',
  emotion: 'Wonder'
}, {
  label: 'Protection',
  portal: 'Hari Singh Nalwa',
  emotion: 'Awe'
}, {
  label: 'Pluralism',
  portal: 'Fakir Azizuddin',
  emotion: 'Connection'
}, {
  label: 'Continuity',
  portal: 'Maharani Jind Kaur',
  emotion: 'Emotion'
}, {
  label: 'Court Return',
  portal: 'Lahore Darbar',
  emotion: 'Unity'
}];
export function StoryArcSection() {
  return <section id="story-arc" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-213">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-214">
        {sectionHeader('Section 04', 'Story', 'Arc', 'Five portals function as one connected cinematic arc — building toward the final court return.')}

        {/* Arc visualization */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-16" data-id="element-215">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-216">
            {arcPhases.map((phase, i) => <div key={i} className={`border p-5 text-center transition-all ${i === 5 ? 'border-[#d4a017]/40 bg-[#d4a017]/10' : 'border-[#d4a017]/10 bg-[#0f0f23]/40'}`} data-id="element-217">
                <p className="font-serif text-2xl text-[#d4a017]/30 mb-2" data-id="element-218">
                  {i + 1}
                </p>
                <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-219">
                  {phase.label}
                </p>
                <p className="text-[#f5f0e8]/30 text-[10px] tracking-wider uppercase mb-2" data-id="element-220">
                  {phase.portal}
                </p>
                <div className="w-8 h-px bg-[#d4a017]/20 mx-auto mb-2" data-id="element-221"></div>
                <p className="text-[#d4a017]/60 text-xs italic" data-id="element-222">
                  {phase.emotion}
                </p>
              </div>)}
          </div>
          {/* Arrow flow */}
          <div className="hidden lg:flex items-center justify-center mt-4 gap-1" data-id="element-223">
            {[0, 1, 2, 3, 4].map(i => <Fragment key={i} data-id="element-224">
                <div className="w-16 h-px bg-[#d4a017]/20" data-id="element-225"></div>
                <ArrowRightIcon className="w-3 h-3 text-[#d4a017]/30" data-id="element-226" />
              </Fragment>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10" data-id="element-227">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed text-center max-w-3xl mx-auto" data-id="element-228">
            The emotional arc builds from{' '}
            <strong className="text-[#d4a017]" data-id="element-229">awakening</strong> through{' '}
            <strong className="text-[#d4a017]" data-id="element-230">wonder</strong>,{' '}
            <strong className="text-[#d4a017]" data-id="element-231">awe</strong>, and{' '}
            <strong className="text-[#d4a017]" data-id="element-232">connection</strong>, reaching its
            emotional peak with{' '}
            <strong className="text-[#d4a017]" data-id="element-233">continuity</strong> — before
            culminating in the collective{' '}
            <strong className="text-[#d4a017]" data-id="element-234">unity</strong> of the final court
            return. Each portal restores a fragment of the shattered Lahore
            Darbar portrait, and only when all five are complete does the full
            painting come alive.
          </p>
        </motion.div>
      </div>
    </section>;
}
/* ─── FLOOR MAP ─── */
export function FloorMapSection() {
  return <section id="floor-map" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-235">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-236"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-237">
        {sectionHeader('Section 05', 'Living', 'Floor Map', 'The floor becomes a living map of Punjab — five rivers, city nodes, and activation circles guide the journey.')}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-238">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 aspect-square flex items-center justify-center relative overflow-hidden" data-id="element-239">
            <div className="absolute inset-0 opacity-10" data-id="element-240">
              <svg viewBox="0 0 400 400" className="w-full h-full" data-id="element-241">
                <path d="M 50, 200 Q 100, 100 200, 150 T 350, 200" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.5" data-id="element-242" />
                <path d="M 80, 250 Q 150, 150 250, 180 T 380, 220" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-243" />
                <path d="M 60, 300 Q 130, 200 230, 230 T 370, 260" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-244" />
                <path d="M 40, 170 Q 120, 80 220, 120 T 360, 170" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-245" />
                <path d="M 70, 140 Q 140, 60 240, 100 T 370, 140" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.2" data-id="element-246" />
                <circle cx="200" cy="180" r="20" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.6" data-id="element-247" />
                <circle cx="120" cy="220" r="12" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-248" />
                <circle cx="280" cy="200" r="12" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.4" data-id="element-249" />
                <circle cx="160" cy="140" r="10" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-250" />
                <circle cx="250" cy="260" r="10" stroke="#d4a017" fill="none" strokeWidth="1" opacity="0.3" data-id="element-251" />
              </svg>
            </div>
            <div className="text-center z-10 p-8" data-id="element-252">
              <MapIcon className="w-12 h-12 text-[#d4a017]/30 mx-auto mb-4" data-id="element-253" />
              <p className="text-[#f5f0e8]/30 text-sm font-light" data-id="element-254">
                Interactive Floor Map
              </p>
              <p className="text-[#f5f0e8]/20 text-xs font-light mt-1" data-id="element-255">
                Projected onto the venue floor
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="space-y-5" data-id="element-256">
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-257">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-258">
                Five Rivers
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-259">
                The five rivers of Punjab — Jhelum, Chenab, Ravi, Beas, and
                Sutlej — glow as luminous pathways across the floor, defining
                the geography of the empire.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-260">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-261">
                City Nodes
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-262">
                Key cities — Lahore, Amritsar, Peshawar, Multan, Kashmir —
                appear as pulsing nodes. Each connects to a portal character and
                their historical significance.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-263">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-264">
                Activation Circles
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-265">
                Five activation circles mark the portal locations. When guests
                gather on a circle, the portal sequence begins — the floor
                reacts, the walls transform, and the story unfolds.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-266">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-267">
                Wayfinding
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-268">
                Luminous routes guide guests between portals. The map itself is
                a storytelling device — showing the empire's reach and the
                connections between its greatest figures.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── FINAL REVEAL ─── */
export function FinalRevealSection() {
  return <section id="final-reveal" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-269">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-270">
        {sectionHeader('Section 06', 'The Final', 'Reveal', 'Five fragments unite. The Lahore Darbar awakens. The court assembles.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-271">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-272">
            <p className="text-[#d4a017]/50 text-[10px] tracking-[0.2em] uppercase mb-4" data-id="element-273">
              Before
            </p>
            <div className="aspect-[3/2] bg-[#0a0a0f] border border-[#d4a017]/10 flex items-center justify-center mb-4" data-id="element-274">
              <div className="grid grid-cols-3 grid-rows-2 gap-2 p-8 w-full h-full" data-id="element-275">
                <div className="bg-[#d4a017]/5 border border-[#d4a017]/10" data-id="element-276"></div>
                <div className="bg-transparent" data-id="element-277"></div>
              </div>
            </div>
            <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-278">
              The portrait begins fragmented — five pieces missing, scattered
              across the portals.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border border-[#d4a017]/30 bg-[#d4a017]/5 p-8" data-id="element-279">
            <p className="text-[#d4a017] text-[10px] tracking-[0.2em] uppercase mb-4" data-id="element-280">
              After
            </p>
            <div className="aspect-[3/2] bg-[#0a0a0f] border border-[#d4a017]/20 flex items-center justify-center mb-4" data-id="element-281">
              <div className="bg-gradient-to-br from-[#d4a017]/20 via-[#d4a017]/10 to-[#8b6914]/10 w-full h-full flex items-center justify-center" data-id="element-282">
                <CrownIcon className="w-16 h-16 text-[#d4a017]/40" data-id="element-283" />
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-284">
              The complete Lahore Darbar — alive, luminous, and projected across
              the entire room.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 space-y-4" data-id="element-285">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-286">
            <div data-id="element-287">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-288">
                Portrait Restoration
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-289">
                Each portal restores one fragment. The portrait assembles piece
                by piece across the evening — visible to all guests as a shared
                progress marker.
              </p>
            </div>
            <div data-id="element-290">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-291">
                Unity Gesture
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-292">
                A collective gesture from all guests triggers the final reveal —
                the room transforms, the court assembles, and Maharaja Ranjit
                Singh appears in full glory.
              </p>
            </div>
            <div data-id="element-293">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-294">
                Bonus Moment
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light leading-relaxed" data-id="element-295">
                A surprise portrait of Maharaja Ranjit Singh himself — a final
                emotional beat that brings the entire evening to its crescendo.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}